"use client";

import { useEffect } from "react";
import { useSearchParams } from "next/navigation";
import { setSecure } from "@/lib/secure-storage";

/**
 * Generate a simple browser fingerprint for tracking
 */
function generateFingerprint(): string {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (ctx) {
    ctx.textBaseline = "top";
    ctx.font = "14px 'Arial'";
    ctx.textBaseline = "alphabetic";
    ctx.fillStyle = "#f60";
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = "#069";
    ctx.fillText("FrenFlow", 2, 15);
    ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
    ctx.fillText("FrenFlow", 4, 17);
  }

  const canvasData = canvas.toDataURL();

  const fingerprint = {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    screenResolution: `${screen.width}x${screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    canvasFingerprint: canvasData.slice(0, 100), // Use first 100 chars for brevity
  };

  // Create a hash of the fingerprint
  const str = JSON.stringify(fingerprint);
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }

  return Math.abs(hash).toString(36);
}

/**
 * Generate a unique session ID for this browsing session
 * Uses crypto.getRandomValues for cryptographically secure randomness
 */
function getSessionId(): string {
  let sessionId = sessionStorage.getItem("frenflow_session_id");
  if (!sessionId) {
    // SECURITY: Use crypto.getRandomValues instead of Math.random for secure IDs
    const randomBytes = new Uint8Array(16);
    crypto.getRandomValues(randomBytes);
    const randomPart = Array.from(randomBytes, b => b.toString(16).padStart(2, '0')).join('');
    sessionId = `${Date.now()}-${randomPart}`;
    sessionStorage.setItem("frenflow_session_id", sessionId);
  }
  return sessionId;
}

/**
 * ReferralTracker component
 * Automatically tracks referral clicks when users visit with a referral parameter.
 * - ?fren= is the current canonical referral parameter (new branding)
 * - ?ref= is kept for backwards compatibility with historic links
 * - ?invite= is kept for legacy invite-code links
 * Also sets up localStorage and cookies for tracking
 */
export default function ReferralTracker() {
  const searchParams = useSearchParams();

  useEffect(() => {
    const trackReferral = async () => {
      // Read referral code from URL. Prefer the new ?fren= param;
      // fall back to ?ref= / ?invite= for older links already in the wild.
      const refCode =
        searchParams?.get("fren") ||
        searchParams?.get("ref") ||
        searchParams?.get("invite");

      if (!refCode) {
        return;
      }

      try {
        // Generate fingerprint and session ID
        const fingerprint = generateFingerprint();
        const sessionId = getSessionId();

        // Track the referral click
        const response = await fetch("/api/referral/track-click", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            code: refCode,
            fingerprint,
            sessionId,
            landing: window.location.pathname,
          }),
        });

        const data = await response.json();

        if (data.success) {
          // Store referral info for signup tracking
          setSecure("frenflow_ref_code", refCode);
          if (data.clickId) {
            localStorage.setItem("frenflow_ref_click_id", data.clickId.toString());
          }
          localStorage.setItem("frenflow_ref_timestamp", Date.now().toString());

        } else {
        }
      } catch (error) {
      }
    };

    trackReferral();
  }, [searchParams]);

  // This component doesn't render anything
  return null;
}
